import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Plus, X, ArrowRight, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";
import { INFO } from "@/data/site";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, size = 18, interactive = false }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type={interactive ? "button" : undefined}
        disabled={!interactive}
        data-testid={interactive ? `star-${i}` : undefined}
        onClick={interactive ? () => onChange(i) : undefined}
        className={interactive ? "transition-transform duration-200 hover:scale-110" : "cursor-default"}
      >
        <Star
          size={size}
          strokeWidth={1.5}
          className={i <= value ? "fill-primary text-primary" : "text-foreground/25"}
        />
      </button>
    ))}
  </div>
);

const resizeImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        let { width, height } = img;
        if (width > max || height > max) {
          if (width > height) { height = (height * max) / width; width = max; }
          else { width = (width * max) / height; height = max; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 0, text: "" });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - images.length);
    const resized = await Promise.all(files.map(resizeImage));
    setImages((prev) => [...prev, ...resized].slice(0, 4));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.rating || !form.text) {
      toast.error("Bitte Name, Bewertung und Text angeben.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, images });
      toast.success("Danke für deine Bewertung!");
      setForm({ name: "", rating: 0, text: "" });
      setImages([]);
      setOpen(false);
      load();
    } catch {
      toast.error("Etwas ist schiefgelaufen. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;
  const { t } = useLang();

  return (
    <section id="bewertungen" data-testid="reviews-section" className="bg-secondary">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("reviews.eyebrow")}</span>
              <h2 className="font-serif font-light text-4xl md:text-6xl tracking-tight mt-4">{t("reviews.title")}</h2>
              {avg && (
                <div className="flex items-center gap-3 mt-4">
                  <Stars value={Math.round(avg)} />
                  <span className="text-sm text-foreground/70">{avg} · {reviews.length}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
            <a data-testid="google-review-btn" href={INFO.googleReviewsUrl} target="_blank" rel="noreferrer" className="group flex items-center gap-3 border border-foreground/30 px-7 py-4 hover:border-foreground transition-colors duration-300">
              <span className="text-sm tracking-wide">{t("reviews.on_google")}</span>
              <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-review-btn" className="group flex items-center gap-3 border border-foreground px-7 py-4">
                  <span className="text-sm tracking-wide">{t("reviews.write")}</span>
                  <ArrowRight strokeWidth={1.5} size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </DialogTrigger>
              <DialogContent data-testid="review-dialog" className="rounded-none border-border max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-3xl font-light">{t("reviews.write")}</DialogTitle>
                  <DialogDescription className="sr-only">Review</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-5 mt-2">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("reviews.eyebrow")}</label>
                    <div className="mt-3"><Stars value={form.rating} onChange={(r) => setForm((f) => ({ ...f, rating: r }))} interactive size={28} /></div>
                  </div>
                  <Input data-testid="review-name" placeholder={t("reviews.name")} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-13 rounded-none bg-background border-border py-3" />
                  <Textarea data-testid="review-text" placeholder={t("reviews.text")} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} className="rounded-none bg-background border-border min-h-[120px]" />

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("reviews.photos")}</label>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {images.map((src, i) => (
                        <div key={i} className="relative w-20 h-20">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-foreground text-background w-5 h-5 flex items-center justify-center">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {images.length < 4 && (
                        <button type="button" data-testid="review-add-photo" onClick={() => fileRef.current?.click()} className="w-20 h-20 border border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
                          <Plus strokeWidth={1.5} />
                        </button>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="review-file-input" />
                    </div>
                  </div>

                  <button type="submit" data-testid="review-submit-btn" disabled={loading} className="group w-full flex items-center justify-between gap-4 bg-primary text-primary-foreground px-7 py-4 disabled:opacity-60">
                    <span className="text-sm tracking-wide">{loading ? "…" : t("reviews.submit")}</span>
                    <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </Reveal>

        {reviews.length === 0 ? (
          <Reveal>
            <div data-testid="reviews-empty" className="border border-dashed border-border py-20 text-center">
              <Quote className="mx-auto text-primary/50" strokeWidth={1} size={40} />
              <p className="mt-4 font-serif text-2xl text-foreground/70">Sei die Erste, die eine Bewertung hinterlässt.</p>
            </div>
          </Reveal>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08}>
                <motion.div data-testid="review-card" className="break-inside-avoid bg-background border border-border p-7">
                  <div className="flex items-center justify-between">
                    <Stars value={r.rating} size={16} />
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("de-DE")}</span>
                  </div>
                  <p className="mt-4 text-foreground/80 leading-relaxed">{r.text}</p>
                  {r.images?.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {r.images.map((src, idx) => (
                        <img key={idx} src={src} alt="" className="w-full aspect-square object-cover" />
                      ))}
                    </div>
                  )}
                  <p className="mt-5 font-serif text-lg">— {r.name}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
